import { snakeCaseToTitleCase } from "common/utils/strings";
import uid from "common/utils/uid";
import { optionSetSelectedOptionId } from "doc/options/optionSetTools";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_124: DocMigration = {
  maxSchemaVersion: "0.0.124",
  description: "Migrates default electrical rules to the base setting set.",
  apply(json: any) {
    // Create a first settings set if it doesn't exist. Reference first settings set id.
    let firstSettingsSetId: string | undefined;
    for (const settingsSetId in json.settingsSets) {
      const settingsSet = json.settingsSets[settingsSetId];
      if (settingsSet.sortIndex !== 0) {
        continue;
      }
      firstSettingsSetId = settingsSet.id;
    }

    if (firstSettingsSetId === undefined) {
      const baseSettingsSetId = uid();
      (json.settingsSets as any) = {};
      (json.settingsSets[baseSettingsSetId] as any) = {
        entityType: "SETTINGS_SET",
        name: "Base Settings Set",
        id: baseSettingsSetId,
        sortIndex: 0,
      };
      firstSettingsSetId = baseSettingsSetId;
    }

    // Determine if an existing electrical settings set exists in the base settings set.
    let electricalSettingsId = Object.keys(json.settings ?? {}).find((key) => {
      const setting = json.settings[key];
      return setting.parentId === firstSettingsSetId && setting.type === "ELECTRICAL_SETTINGS";
    });

    // If no settings for electrical exist in the base settings set, create them. Set
    // 'mergeRules' to true so that in the next step, any existing default rules will merge
    // into these settings.
    if (electricalSettingsId === undefined) {
      electricalSettingsId = uid();
      (json.settings[electricalSettingsId] as any) = {
        id: electricalSettingsId,
        entityType: "SETTINGS",
        sortIndex: 0,
        parentId: firstSettingsSetId,
        name: snakeCaseToTitleCase("ELECTRICAL_SETTINGS"),
        type: "ELECTRICAL_SETTINGS",
        rules: [],
        mergeRules: true,
      };
    }

    // Find the builder settings id that references the current default settings.
    const defaultElevationId = optionSetSelectedOptionId(json.params, json.design, "elevationStyle");
    const defaultElevation = defaultElevationId && json.elevations && json.elevations[defaultElevationId];
    const builderSettingsId = defaultElevation?.baseSettings;

    // If 'mergeRules' is enabled on the electrical settings, merge in any existing default rules.
    // If 'meregeRules' isn't set, there's no need to modify the settings.
    const defaultElectricalRules =
      (builderSettingsId && builtinElectricalSettings[builderSettingsId]?.rules) || defaultElectricalSettings.rules;
    const electricalSettings = json.settings[electricalSettingsId] as any;
    if (defaultElectricalRules && electricalSettings.mergeRules) {
      if (electricalSettings.rules === undefined) {
        electricalSettings.rules = [];
      }
      electricalSettings.rules.unshift(...defaultElectricalRules);
    }

    // In all cases, disable mergeRules on the electrical settings, as the defaults are now exposed on the settings set.
    electricalSettings.mergeRules = false;
    return json;
  },
};

export default migration_0_0_124;

const defaultElectricalSlots = {
  controllers: {
    primary: "SWITCH",
    sensor: "SENSOR",
  },
  loads: {
    primaryLight: "MOUNTED_CEILING_LIGHT",
    secondaryLight: "RECESSED_CEILING_LIGHT",

    outlet: "OUTLET",
    outletGFCI: "OUTLET_GFCI",
    outletHDMI: "OUTLET_HDMI",

    recessedCeilingLight: "RECESSED_CEILING_LIGHT",

    pendantCeilingLight: "PENDANT_CEILING_LIGHT",
    keylessCeilingLight: "KEYLESS_CEILING_LIGHT",

    flourescentCeilingLight: "FLOURESCENT_CEILING_LIGHT",

    trackCeilingLight: "TRACK_CEILING_LIGHT",

    mountedWallLight: "MOUNTED_WALL_LIGHT",

    fanCeiling: "CEILING_FAN",

    smokeDetector: "SMOKE_DETECTOR",

    carbonMonoxideDetector: "CARBON_MONOXIDE_DETECTOR",

    thermostat: "THERMOSTAT",

    bathFan: "BATH_FAN",

    cableOutlet: "CABLE_OUTLET",
    floodLight: "FLOOD_LIGHT",

    outletGFCIwaterProof: "OUTLET_GFCI_WP",
    outletFloor: "OUTLET_FLOOR",

    garageDoorOpener: "GARAGE_DOOR_OPENER",

    dishDisposal: "DISH_DISPOSAL",

    doorbellChime: "DOORBELL_CHIME",

    outlet240V: "OUTLET_240V",
    outlet220V: "OUTLET_220V",
    outletCAT5: "OUTLET_CAT5",

    hoseBib: "HOSE_BIB",
  },
};

const redTreeElectricalSettings = {
  type: "ELECTRICAL_SETTINGS",
  slots: defaultElectricalSlots,
  rules: [],
};

const essexElectricalSettings = {
  type: "ELECTRICAL_SETTINGS",
  slots: defaultElectricalSlots,
  rules: [
    {
      id: "8wKWlj818TLY5zfe",
      filters: {
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
        outletSpacing: 120,
      },
    },
    {
      id: "8MN2kbTYlBxWWWWI",
      filters: {
        spaceType: [{ spaceType: "EXTERIOR_COVERED" }],
        roomType: [
          { roomType: "FRONT_PORCH", negate: true },
          { roomType: "SOLARIUM", negate: true },
        ],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE_OUTSIDE_ROOMSET" }],
      },
    },
    {
      id: "RU3UW0WnpFjW11jv",
      filters: {
        roomType: [{ roomType: "SOLARIUM" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "EXPLICIT POSITION",
            explicitQueryPoint: {
              type: "LEGACY",
              roomId: "rearPorch-sunroom",
              edgeId: "0",
              param: 0.75,
              paramNormalized: true,
              reverse: false,
            },
            snapToCandidateMaxSearchDistance: 30,
          },
        ],
      },
    },
    {
      id: "aSenLDxc37ckytN7",
      filters: {
        roomType: [{ roomType: "FRONT_PORCH" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "EXPLICIT POSITION",
            explicitQueryPoint: {
              type: "LEGACY",
              roomId: "entry-foyer",
              edgeId: "3",
              param: 20,
              paramNormalized: false,
              reverse: true,
            },
            snapToCandidateMaxSearchDistance: 30,
          },
        ],
      },
    },
    {
      id: "tTPMw91mzSWlO7az",
      filters: {
        roomType: [{ roomType: "MASTER_BED" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
        addCeilingFan: true,
      },
    },
    {
      id: "lRTIfWnjNVuoFrDW",
      filters: {
        spaceType: [{ spaceType: "BEDROOM" }],
        roomType: [{ roomType: "MASTER_BED", negate: true }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
        addCeilingFan: true,
      },
    },
    {
      id: "8YAuzDtonLlLAgOk",
      filters: {
        spaceType: [{ spaceType: "CONTEMPLATIVE" }],
        roomType: [{ roomType: "FLEX", negate: true }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "A3fS144qWZKy4snf",
      filters: {
        roomType: [{ roomType: "FLEX" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "PRIMARY_ENTRANCE",
            queryPoint: {
              type: "LEGACY",
              roomId: "bedroom-flex",
              edgeId: "3",
              param: 0.5,
              paramNormalized: true,
              reverse: false,
            },
          },
        ],
        addCeilingFan: true,
      },
    },
    {
      id: "TIxMGlDrOuIgpJMM",
      filters: {
        spaceType: [{ spaceType: "STORAGE" }],
        width: [{ minimum: 48 }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "AKFFsh1feKHH6Z1G",
      filters: {
        roomType: [{ roomType: "GARAGE" }],
      },
      electricalLayout: {
        layoutType: "GARAGE_LIGHTS",
        addOpener: true,
        controllerPlacementType: [
          { type: "PRIMARY_ENTRANCE_OUTSIDE_ROOMSET" },
          {
            type: "EXPLICIT POSITION",
            explicitQueryPoint: {
              type: "LEGACY",
              roomId: "kitchenWithNoStair-JUMP0",
              edgeId: "0",
              param: 3,
              paramNormalized: false,
              reverse: false,
            },
            snapToCandidateMaxSearchDistance: 0,
          },
        ],
      },
    },
    {
      id: "WYvjp9pR58JWOeCL",
      filters: {
        roomType: [{ roomType: "LAUNDRY" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "0CjTcH6FjNqAHi7z",
      filters: { roomType: [{ roomType: "WATER_CLOSET" }] },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "sQQGgrffdVtZYDYC",
      filters: {
        roomType: [{ roomType: "MASTER_VESTIBULE" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "kMAw1ZC0rv247DkT",
      filters: {
        roomType: [{ roomType: "DINING" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "EXPLICIT POSITION",
            explicitQueryPoint: {
              type: "LEGACY",
              roomId: "livingTop-general",
              edgeId: "1",
              param: 36,
              paramNormalized: false,
              reverse: false,
            },
            snapToCandidateMaxSearchDistance: 60,
          },
        ],
      },
    },
    {
      id: "Bo2erfp0u4kmH6S3",
      filters: {
        roomType: [{ roomType: "JUMP" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "NEAREST",
            queryPoint: {
              type: "LEGACY",
              roomId: "livingTop-general",
              edgeId: "3",
              param: 0.5,
              paramNormalized: true,
              reverse: false,
            },
          },
        ],
      },
    },
    {
      id: "1Xw6F7BLEHIbD9mT",
      filters: {
        roomType: [{ roomType: "HALL_CASED_OPENING" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "PRIMARY_ENTRANCE",
            queryPoint: {
              type: "LEGACY",
              roomId: "bedroom-HALL_CASED_OPENING0",
              edgeId: "1",
              param: 0.3,
              paramNormalized: true,
              reverse: false,
            },
          },
        ],
      },
    },
  ],
};

const elliottElectricalSlots = {
  controllers: {
    primary: "SWITCH",

    sensor: "SENSOR",
  },
  loads: {
    primaryLight: "RECESSED_CEILING_LIGHT",
    secondaryLight: "MOUNTED_CEILING_LIGHT",

    outlet: "OUTLET",
    outletGFCI: "OUTLET_GFCI",

    recessedCeilingLight: "RECESSED_CEILING_LIGHT",

    pendantCeilingLight: "PENDANT_CEILING_LIGHT",

    keylessCeilingLight: "KEYLESS_CEILING_LIGHT",

    flourescentCeilingLight: "FLOURESCENT_CEILING_LIGHT",

    trackCeilingLight: "TRACK_CEILING_LIGHT",

    mountedWallLight: "MOUNTED_WALL_LIGHT",

    fanCeiling: "CEILING_FAN",

    smokeDetector: "SMOKE_DETECTOR",

    carbonMonoxideDetector: "CARBON_MONOXIDE_DETECTOR",

    thermostat: "THERMOSTAT",

    bathFan: "BATH_FAN",

    cableOutlet: "CABLE_OUTLET",

    floodLight: "FLOOD_LIGHT",

    outletGFCIwaterProof: "OUTLET_GFCI_WP",

    outletFloor: "OUTLET_FLOOR",
    outlet220V: "OUTLET_220V",
    outletCAT5: "OUTLET_CAT5",

    garageDoorOpener: "GARAGE_DOOR_OPENER",

    dishDisposal: "DISH_DISPOSAL",

    doorbellChime: "DOORBELL_CHIME",

    outlet240V: "OUTLET_240V",

    outletHDMI: "OUTLET_HDMI",

    hoseBib: "HOSE_BIB",
  },
};

const defaultElectricalSettings = {
  type: "ELECTRICAL_SETTINGS",
  slots: defaultElectricalSlots,
  rules: [
    {
      id: "B6hDNutsUZouooWs",
      filters: {
        spaceType: [{ spaceType: "BEDROOM" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
        singleOutletMax: 144,
        minWallLength: 24,
        outletSpacing: 72,
      },
    },
    {
      id: "IoBQytZYWWsA7x6r",
      filters: {
        spaceType: [{ spaceType: "DINING" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
      },
    },
    {
      id: "eeNEZmZ3XVt6uCjv",
      filters: {
        spaceType: [{ spaceType: "LIVING" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
      },
    },
    {
      id: "PNwZRsdKfAujTHDJ",
      filters: {
        spaceType: [{ spaceType: "CIRCULATION" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
      },
    },
    {
      id: "EBQIzNqW1VllQp5n",
      filters: {
        roomType: [{ roomType: "GARAGE" }],
        area: [{ minimum: 300 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
        loadType: "OUTLET_GFCI",
        singleOutletMax: 300,
      },
    },
    {
      id: "MmNfMCyHIlnE4xam",
      filters: {
        spaceType: [{ spaceType: "TRANSITION" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "STANDARD_OUTLETS",
        singleOutletMax: 240,
        minWallLength: 72,
      },
    },
    {
      id: "lzOKjtNciJeC9SRq",
      filters: {
        spaceType: [{ spaceType: "EXTERIOR_COVERED" }],
        roomType: [
          { roomType: "FRONT_PORCH", negate: true },
          { roomType: "SOLARIUM", negate: true },
        ],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE_OUTSIDE_ROOMSET" }],
      },
    },
    {
      id: "2R6VOzUuSy2UlyO4",
      filters: {
        roomType: [{ roomType: "MASTER_BED" }],
      },
      electricalLayout: {
        layoutType: "FOUR_LIGHTS",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
        addFan: true,
      },
    },
    {
      id: "2S8WecGNSsS3a3qL",
      filters: {
        roomType: [{ roomType: "PATIO" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
      },
    },
    {
      id: "9dJcWY4WLsxDovRy",
      filters: {
        roomType: [{ roomType: "FRONT_PORCH" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
      },
    },
    {
      id: "GKgJaM7lTkWj2evH",
      filters: {
        roomType: [{ roomType: "REAR_PORCH" }],
        area: [{ minimum: 100 }],
      },
      electricalLayout: {
        layoutType: "FOUR_LIGHTS",
        addFan: true,
        controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
      },
    },
    {
      id: "1WlwyCAjo3TtzUAt",
      filters: {
        roomType: [{ roomType: "POWDER" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "WcMxFqm50dCjXewd",
      filters: {
        roomType: [{ roomType: "SHOWER" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [
          {
            type: "NEAREST",
            candidateTypeConstraints: "BY_DOOR",
          },
        ],
      },
    },
    {
      id: "WIn10baYW0EZ6grI",
      filters: {
        roomType: [{ roomType: "WATER_CLOSET" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "JINPzv9u9lu3tuHN",
      filters: {
        roomType: [{ roomType: "WATER_CLOSET" }, { roomType: "BATHFULL" }, { roomType: "POWDER" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        loadType: "BATH_FAN",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "Vk7ONgPknZyKO4Cr",
      filters: {
        spaceType: [{ spaceType: "BEDROOM" }],
        roomType: [
          { roomType: "MASTER_BED", negate: true },
          { roomType: "BED_PASS", negate: true },
        ],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "ysSDoMiR96q63mXG",
      filters: {
        spaceType: [{ spaceType: "CONTEMPLATIVE" }],
        roomType: [{ roomType: "FLEX", negate: true }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },

    {
      id: "npfrc8TFUBEW1Yeg",
      filters: {
        spaceType: [{ spaceType: "STORAGE" }],
        width: [{ minimum: 48 }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "jRkaYLYWoeRmEa3M",
      filters: {
        roomType: [{ roomType: "HALL" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "NEAREST" }],
      },
    },
    {
      id: "kqTeYEWUIvYq7oyI",
      filters: {
        roomType: [{ roomType: "LAUNDRY" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "vCiWYRyHiwkD22N3",
      filters: {
        roomType: [{ roomType: "MASTER_VESTIBULE" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      },
    },
    {
      id: "jk2l5aWcW4oFq8Yl",
      filters: {
        roomType: [{ roomType: "DINING" }],
      },
      electricalLayout: {
        layoutType: "SINGLE_LIGHT",
        controllerPlacementType: [{ type: "NEAREST" }],
      },
    },
    {
      id: "6JwekGWXhSEY71bV",
      filters: {
        roomType: [{ roomType: "LIVING" }],
      },
      electricalLayout: {
        layoutType: "FOUR_LIGHTS",
        addFan: true,
        controllerPlacementType: [
          {
            type: "NEAREST",
          },
        ],
      },
    },
    {
      id: "358HEPWjfgvBV30o",
      filters: {
        roomType: [{ roomType: "KITCHEN" }],
      },
      electricalLayout: {
        layoutType: "FOUR_LIGHTS",
        cornerInset: 42,
        addFan: false,
        controllerPlacementType: [
          {
            type: "NEAREST",
          },
        ],
      },
    },
  ],
};

const elliottElectrical = {
  type: "ELECTRICAL_SETTINGS",
  slots: elliottElectricalSlots,
  rules: defaultElectricalSettings.rules,
};

const builtinElectricalSettings: { [id: string]: any } = {
  ["RedTree_001"]: redTreeElectricalSettings,
  ["RedTree_002"]: redTreeElectricalSettings,
  ["RedTree_003"]: redTreeElectricalSettings,
  ["Elliott_H00"]: elliottElectrical,
  ["Elliott_H10"]: elliottElectrical,
  ["Elliott_H20"]: elliottElectrical,
  ["Elliott_J00"]: elliottElectrical,
  ["Elliott_F00"]: elliottElectrical,
  ["Essex_W1A"]: essexElectricalSettings,
};
